import React from "react";
import { Game, rankPlayers, sharedPositions } from "../types";

// ── Illan pistetaulukko ───────────────────────────────────────
//
// Yksi toteutus pelaajalle ja ylläpitäjälle. Tätä ennen niitä oli kaksi,
// ja ne erkanivat juuri siinä kohdassa jolla on eniten merkitystä:
//
//   PlayerView:  const sorted = ranking.map(r => [r.name, r.wins]);
//                ...  <span>{points} pts</span>
//
// Pelaajan näkymä siis näytti **voitot** ja kutsui niitä pisteiksi.
// 1.11.2026 illassa kaikilla neljällä oli 3 voittoa, joten pelaaja näki
// "3 pts" neljä kertaa, kun oikeat pisteet olivat 52, 50, 48 ja 46.
// Ylläpitäjän näkymä näytti saman illan oikein. Pelaajat laskivat illan
// käsin, koska sovellus ei kertonut mistä sijoitus syntyi — ja juuri siitä
// syntyi koko kiista kauden laskennasta.
//
// Taulukossa on **vain pisteet**, koska ne ratkaisevat sijoituksen
// (klubin päätös 2.11.2026, docs/liiga.md). Voitot jätettiin pois
// tarkoituksella: 1.11. illassa kaikilla neljällä oli 3V–3H, jolloin
// voittoluku ei erottanut ketään mutta näytti siltä kuin sen pitäisi.
// Se aiheutti enemmän kysymyksiä kuin vastauksia.
//
// Poikkeus: **jos kaksi pelaajaa on tasapisteissä**, voitot ratkaisevat
// sijan, ja silloin ne näytetään koko taulukossa. Muuten sija näyttäisi
// mielivaltaiselta — kaksi samaa lukua, kaksi eri sijaa, eikä mitään
// mikä kertoisi miksi. Voitot näkyvät myös kauden taulukossa rivin
// erittelyssä, joten ne eivät katoa mihinkään.
//
// Sijoitus tulee `sharedPositions`ista, joten tasapisteet jakavat saman
// sijan: 1. 1. 3., ei 1. 2. 3.

interface Props {
  games: Game[];
  /** Osallistujien nimet. Pelaamatta jäänyt näkyy ilman sijoitusta. */
  names: string[];
  lang?: "fi" | "en";
  /** Oma rivi korostetaan, jos nimi täsmää. */
  myName?: string;
}

const MITALIT = ["🥇", "🥈", "🥉"];

const C = {
  text: "var(--sl-text)", muted: "var(--sl-muted)", navy: "var(--sl-primary)",
  gold: "var(--sl-accent)", soft: "var(--sl-soft)", border: "var(--sl-border)",
};

/** Onko illassa yksikään ratkennut ottelu. Tyhjää taulukkoa ei näytetä. */
export function nightHasResults(games: Game[], names: string[]): boolean {
  return rankPlayers(games, names).some(r => r.played > 0);
}

const NightStandings: React.FC<Props> = ({ games, names, lang = "fi", myName }) => {
  const fi = lang !== "en";
  const ranking = rankPlayers(games, names);
  const positions = sharedPositions(ranking);
  const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
  // Voitot näytetään vain jos ne ratkaisevat jonkun sijan, eli jos
  // kaksi pelannutta on tasapisteissä.
  const pelanneet = ranking.filter(r => r.played > 0);
  const voitotNakyviin = pelanneet.some((r, i) =>
    pelanneet.some((o, j) => i !== j && o.pts === r.pts));

  return (
    <>
      {ranking.map((r, i) => {
        const karki = positions[i] === 1 && r.played > 0;
        const oma = !!myName && same(r.name, myName);
        return (
          <div key={r.name} style={{
            display: "flex", alignItems: "center", gap: 10,
            background: karki ? "var(--sl-accent-a10)" : C.soft,
            border: `1px solid ${karki ? "var(--sl-accent-a20)" : C.border}`,
            borderRadius: 10, padding: "9px 12px", marginBottom: 6,
          }}>
            <div style={{ fontSize: 18, width: 26, textAlign: "center" }}>
              {r.played === 0
                ? <span style={{ fontSize: 13, color: C.muted }}>–</span>
                : MITALIT[positions[i] - 1] ?? `${positions[i]}.`}
            </div>
            <div style={{
              flex: 1, fontSize: 13, fontWeight: oma ? 800 : 700,
              color: karki ? C.gold : C.text,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {r.name}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: C.navy, fontFamily: "monospace", lineHeight: 1.2 }}>
                {r.pts} {fi ? "p" : "pts"}
              </div>
              {voitotNakyviin && r.played > 0 && (
                <div style={{ fontSize: 11, color: C.muted }}>
                  {fi ? `${r.wins}V – ${r.losses}H` : `${r.wins}W – ${r.losses}L`}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default NightStandings;
